import React, { useState } from 'react';
import './RemoveUserFromGroupModal.css';

const RemoveUserFromGroupModal = ({ group, users, onClose, onSubmit }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      setError('Please select a user to remove');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(group.cn, selectedUserId);
    } catch (error) {
      console.error('Failed to remove user from group:', error);
      setError('Failed to remove user from group. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
    if (error) {
      setError('');
    }
  };

  // Get users that are members of this group
  const groupMembers = group.member || [];
  const eligibleUsers = users.filter(user => 
    groupMembers.some(member => 
      member.includes(user.uid) || member.includes(user.mail)
    )
  );

  const selectedUser = eligibleUsers.find(user => user.id === selectedUserId);

  return (
    <div className="modal-overlay">
      <div className="modal-content remove-user-modal">
        <div className="modal-header">
          <h3>Remove User from Group</h3>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="group-info">
            <h4>Group Information</h4>
            <div className="group-card">
              <div className="group-name">{group.cn}</div>
              {group.description && (
                <div className="group-description">{group.description}</div>
              )}
              <div className="group-stats">
                <span>Total Members: {groupMembers.length}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="remove-user-form">
            <div className="form-group">
              <label htmlFor="userId" className="form-label">
                Select User to Remove *
              </label>
              
              {eligibleUsers.length === 0 ? (
                <div className="no-users-available">
                  <p>No users available to remove from this group.</p>
                  <p className="help-text">
                    This group either has no members or the member information is not available.
                  </p>
                </div>
              ) : (
                <select
                  id="userId"
                  value={selectedUserId}
                  onChange={handleUserChange}
                  className={`form-select ${error ? 'error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">Choose a user...</option>
                  {eligibleUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} ({user.mail})
                    </option>
                  ))}
                </select>
              )}
              
              {error && (
                <span className="error-message">{error}</span>
              )}
            </div>

            {selectedUser && (
              <div className="selected-user-preview">
                <h4>User to Remove</h4>
                <div className="user-card preview">
                  <div className="user-info">
                    <div className="user-name">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </div>
                    <div className="user-email">{selectedUser.mail}</div>
                    <div className="user-uid">UID: {selectedUser.uid}</div>
                  </div>
                  <div className="user-details">
                    {selectedUser.department && (
                      <span className="detail-tag">Department: {selectedUser.department}</span>
                    )}
                    {selectedUser.title && (
                      <span className="detail-tag">Title: {selectedUser.title}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="warning-message">
              <div className="warning-icon">⚠️</div>
              <div className="warning-text">
                <strong>Warning:</strong> This action will remove the selected user from the group "{group.cn}". 
                The user will lose any permissions or access associated with this group membership.
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-danger"
                disabled={isSubmitting || !selectedUserId || eligibleUsers.length === 0}
              >
                {isSubmitting ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RemoveUserFromGroupModal;
